/**
 * Searchable, relevance-ranked select — single and multiple.
 *
 * WHY THIS EXISTS ALONGSIDE `Select`.
 * `Select` renders a native `<select>`. A native select cannot search, cannot rank, and
 * on a list of ~300 DMAs it degrades into scrolling. Those three are requirements for
 * the MMM scope and guardrail screens, so this is a sibling primitive rather than a
 * prop on the first. Keep using `Select` for short fixed lists — Monday/Sunday,
 * new/refresh; it is lighter, and it is what the rest of the product already looks
 * like. Reach for this one when the list is long, dynamic, or drawn from an earlier
 * step of a wizard.
 *
 * WHAT IT GUARANTEES
 *   Alphabetical by default. Pass `ordered` when the incoming order carries meaning —
 *   weekdays, a hierarchy, a ranked list — and the ranker leaves it untouched.
 *
 *   Relevance while typing, through `@/lib/search`. Prefix beats word-start beats
 *   mid-word, and a multi-word query has to match every word. One ranker behind every
 *   searchable list in the product, so the ordering is the same wherever the user types.
 *
 *   A long result set is CAPPED and says so. Rendering three hundred options is not a
 *   listbox, it is a scrollbar with extra steps. The footer states what is hidden
 *   rather than letting the reader assume they are seeing everything.
 *
 *   Combobox semantics: aria-expanded, aria-activedescendant, a roving highlight,
 *   Home/End, Escape, click-away. Selection carries a check glyph and aria-selected —
 *   never colour alone, the same rule StatusChip follows.
 *
 * No portal. The popup lives in the field's own stacking context, which is how Dialog
 * and the tooltip layer already behave.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { CSSProperties, JSX, KeyboardEvent, ReactNode } from 'react';
import { alphabetical, rank } from '@/lib/search';
import { Button } from './Button';
import { SearchInput, type SelectOption } from './Fields';
import { Icon } from './Icon';

export interface SearchOption extends SelectOption {
  /** Searched at a penalty and shown beside the label. Codes, parents, aliases. */
  readonly hint?: string | undefined;
}

/** Above this many options the search box appears. Below it, it is noise. */
const SEARCH_FROM = 8;
/** Rows rendered at once. Anything beyond this is reported in the footer. */
const MAX_ROWS = 120;

interface SharedProps {
  readonly options: readonly SearchOption[];
  readonly ariaLabel: string;
  readonly placeholder?: string | undefined;
  /** Keep the caller's order rather than sorting alphabetically. */
  readonly ordered?: boolean | undefined;
  readonly disabled?: boolean | undefined;
  readonly searchFrom?: number | undefined;
  readonly style?: CSSProperties | undefined;
  /** Shown when nothing matches. Say what would widen the search. */
  readonly emptyHint?: string | undefined;
}

const searchable = (o: SearchOption): { label: string; hint?: string } =>
  o.hint === undefined ? { label: o.label } : { label: o.label, hint: o.hint };

/** Filter + order, memoised. Alphabetical when idle, relevance-ranked when typing. */
function useMatches(
  options: readonly SearchOption[],
  ordered: boolean,
  query: string,
): readonly SearchOption[] {
  const sorted = useMemo(
    () => (ordered ? options : alphabetical(options, (o) => o.label)),
    [options, ordered],
  );
  return useMemo(() => rank(sorted, query, searchable), [sorted, query]);
}

/** Close when the pointer goes down anywhere outside the field. */
function useClickAway(onAway: () => void): React.RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement | null>(null);
  const latest = useRef(onAway);
  // Written in an effect, not during render: the listener below binds once and
  // this keeps it calling the current closure.
  useEffect(() => {
    latest.current = onAway;
  });
  useEffect(() => {
    const onDown = (e: MouseEvent): void => {
      const node = ref.current;
      if (node !== null && e.target instanceof Node && !node.contains(e.target)) {
        latest.current();
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
    };
  }, []);
  return ref;
}

/** Arrow / Home / End / Escape, shared by both variants. Returns true if handled. */
function navigate(
  e: KeyboardEvent<HTMLDivElement>,
  count: number,
  setActive: (fn: (i: number) => number) => void,
): boolean {
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      setActive((i) => Math.min(i + 1, count - 1));
      return true;
    case 'ArrowUp':
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
      return true;
    case 'Home':
      e.preventDefault();
      setActive(() => 0);
      return true;
    case 'End':
      e.preventDefault();
      setActive(() => Math.max(0, count - 1));
      return true;
    default:
      return false;
  }
}

function Popup({
  id,
  label,
  query,
  onQuery,
  showSearch,
  active,
  onActive,
  shown,
  total,
  emptyHint,
  header,
  children,
}: {
  readonly id: string;
  readonly label: string;
  readonly query: string;
  readonly onQuery: (q: string) => void;
  readonly showSearch: boolean;
  readonly active: number;
  readonly onActive: (i: number) => void;
  readonly shown: number;
  readonly total: number;
  readonly emptyHint?: string | undefined;
  readonly header?: ReactNode | undefined;
  readonly children: ReactNode;
}): JSX.Element {
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    popRef.current?.querySelector('input')?.focus();
  }, []);

  // Keep the highlighted row in view when the keyboard walks past the fold.
  // Feature-detected: jsdom has no scrollIntoView, and an unguarded call there
  // throws inside the effect and takes down every test that mounts this.
  useEffect(() => {
    const row = document.getElementById(`${id}-o${String(active)}`);
    if (typeof row?.scrollIntoView === 'function') row.scrollIntoView({ block: 'nearest' });
  }, [id, active]);

  return (
    <div className="ss-pop" ref={popRef}>
      {showSearch && (
        <div className="ss-search">
          <SearchInput
            value={query}
            // SearchInput derives its accessible name from the placeholder, so this
            // text is what a screen reader announces. Naming the field it belongs to
            // keeps several comboboxes on one screen distinguishable.
            placeholder={`Search ${label}`}
            onChange={(q) => {
              onQuery(q);
              onActive(0);
            }}
          />
        </div>
      )}
      {header}
      <ul className="ss-list" id={`${id}-list`} role="listbox" aria-label="Options">
        {shown === 0 ? (
          <li className="ss-empty caption">
            No match for “{query}”.{emptyHint === undefined ? '' : ` ${emptyHint}`}
          </li>
        ) : (
          children
        )}
      </ul>
      {shown < total && (
        <div className="ss-foot caption">
          Showing {shown} of {total} — keep typing to narrow.
        </div>
      )}
    </div>
  );
}

function Row({
  id,
  index,
  option,
  selected,
  active,
  onHover,
  onPick,
}: {
  readonly id: string;
  readonly index: number;
  readonly option: SearchOption;
  readonly selected: boolean;
  readonly active: boolean;
  readonly onHover: () => void;
  readonly onPick: () => void;
}): JSX.Element {
  const off = option.disabled === true;
  return (
    <li
      id={`${id}-o${String(index)}`}
      role="option"
      aria-selected={selected}
      aria-disabled={off}
      className={['ss-opt', active ? 'now' : '', off ? 'dis' : ''].filter(Boolean).join(' ')}
      onMouseEnter={onHover}
      onClick={onPick}
    >
      <span className="ss-tick">{selected && <Icon name="check" />}</span>
      <span className="ss-lb">
        {option.label}
        {option.hint !== undefined && <span className="caption"> · {option.hint}</span>}
      </span>
    </li>
  );
}

/* ------------------------------------------------------------------ single -- */

export function SearchSelect({
  value,
  onChange,
  options,
  ariaLabel,
  placeholder = 'Select…',
  ordered = false,
  disabled = false,
  searchFrom = SEARCH_FROM,
  style,
  emptyHint,
  clearable = false,
}: SharedProps & {
  readonly value: string;
  /** The label travels with the value — screens that display it need both. */
  readonly onChange: (value: string, label: string) => void;
  readonly clearable?: boolean | undefined;
}): JSX.Element {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const matched = useMatches(options, ordered, query);
  const visible = matched.slice(0, MAX_ROWS);
  const current = options.find((o) => o.value === value);
  const ref = useClickAway(() => {
    setOpen(false);
  });

  const close = (): void => {
    setOpen(false);
    setQuery('');
    setActive(0);
  };

  const pick = (option: SearchOption): void => {
    if (option.disabled === true) return;
    onChange(option.value, option.label);
    close();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'Tab') {
      close();
      return;
    }
    if (navigate(e, visible.length, setActive)) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      const option = visible[active];
      if (option !== undefined) pick(option);
    }
  };

  return (
    <div className="ss" ref={ref} style={style} onKeyDown={onKeyDown}>
      <button
        type="button"
        className="inp ss-trig"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        {...(open && visible[active] !== undefined
          ? { 'aria-activedescendant': `${id}-o${String(active)}` }
          : {})}
        disabled={disabled}
        onClick={() => {
          setOpen((o) => !o);
        }}
      >
        <span className={current === undefined ? 'ss-ph' : undefined}>
          {current?.label ?? placeholder}
        </span>
        {clearable && current !== undefined && (
          <span
            className="ss-x"
            role="button"
            tabIndex={-1}
            aria-label={`Clear ${ariaLabel}`}
            onClick={(e) => {
              e.stopPropagation();
              onChange('', '');
            }}
          >
            <Icon name="x" />
          </span>
        )}
        <Icon name="chevD" />
      </button>

      {open && (
        <Popup
          id={id}
          label={ariaLabel}
          query={query}
          onQuery={setQuery}
          showSearch={options.length >= searchFrom}
          active={active}
          onActive={setActive}
          shown={visible.length}
          total={matched.length}
          emptyHint={emptyHint}
        >
          {visible.map((o, i) => (
            <Row
              key={o.value}
              id={id}
              index={i}
              option={o}
              selected={o.value === value}
              active={i === active}
              onHover={() => {
                setActive(i);
              }}
              onPick={() => {
                pick(o);
              }}
            />
          ))}
        </Popup>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- multiple -- */

export function MultiSearchSelect({
  values,
  onChange,
  options,
  ariaLabel,
  placeholder = 'Select…',
  ordered = false,
  disabled = false,
  searchFrom = SEARCH_FROM,
  style,
  emptyHint,
  bulk = true,
}: SharedProps & {
  readonly values: readonly string[];
  readonly onChange: (values: readonly string[]) => void;
  /** Select-all / clear across whatever the search currently shows. */
  readonly bulk?: boolean | undefined;
}): JSX.Element {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const matched = useMatches(options, ordered, query);
  const visible = matched.slice(0, MAX_ROWS);
  const chosen = new Set(values);
  const ref = useClickAway(() => {
    setOpen(false);
  });

  const toggle = (option: SearchOption): void => {
    if (option.disabled === true) return;
    onChange(
      chosen.has(option.value)
        ? values.filter((v) => v !== option.value)
        : [...values, option.value],
    );
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape' || e.key === 'Tab') {
      if (e.key === 'Escape') e.preventDefault();
      setOpen(false);
      setQuery('');
      return;
    }
    if (navigate(e, visible.length, setActive)) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      // Enter toggles and the list STAYS OPEN. Picking eight DMAs should not
      // mean reopening the list eight times.
      const option = visible[active];
      if (option !== undefined) toggle(option);
    }
  };

  // Past a couple of picks a count beats a wall of labels. `matched` is not used
  // here — the summary describes the selection, not the current search.
  const summary =
    values.length === 0
      ? placeholder
      : values.length <= 2
        ? // Ordered, not raw input order, so the summary reads the way the list does.
          alphabetical(
            options.filter((o) => chosen.has(o.value)),
            (o) => o.label,
          )
            .map((o) => o.label)
            .join(', ')
        : `${values.length} of ${options.length} selected`;

  return (
    <div className="ss" ref={ref} style={style} onKeyDown={onKeyDown}>
      <button
        type="button"
        className="inp ss-trig"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        {...(open && visible[active] !== undefined
          ? { 'aria-activedescendant': `${id}-o${String(active)}` }
          : {})}
        disabled={disabled}
        onClick={() => {
          setOpen((o) => !o);
        }}
      >
        <span className={values.length === 0 ? 'ss-ph' : undefined}>{summary}</span>
        <Icon name="chevD" />
      </button>

      {open && (
        <Popup
          id={id}
          label={ariaLabel}
          query={query}
          onQuery={setQuery}
          showSearch={options.length >= searchFrom}
          active={active}
          onActive={setActive}
          shown={visible.length}
          total={matched.length}
          emptyHint={emptyHint}
          header={
            bulk && visible.length > 0 ? (
              <div className="ss-bulk">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const add = visible.filter((o) => o.disabled !== true).map((o) => o.value);
                    onChange([...new Set([...values, ...add])]);
                  }}
                >
                  {visible.length === matched.length
                    ? 'Select all'
                    : `Select the ${visible.length} shown`}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={values.length === 0}
                  onClick={() => {
                    onChange([]);
                  }}
                >
                  Clear
                </Button>
              </div>
            ) : null
          }
        >
          {visible.map((o, i) => (
            <Row
              key={o.value}
              id={id}
              index={i}
              option={o}
              selected={chosen.has(o.value)}
              active={i === active}
              onHover={() => {
                setActive(i);
              }}
              onPick={() => {
                toggle(o);
              }}
            />
          ))}
        </Popup>
      )}
    </div>
  );
}
